import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import ChatIcon from "@material-ui/icons/Chat";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import MicIcon from "@material-ui/icons/Mic";
import RefreshIcon from "@material-ui/icons/Refresh";
import SendIcon from "@material-ui/icons/Send";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import StopIcon from "@material-ui/icons/Stop";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import openSocket from "../../services/socket-io";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
    borderRadius: 0,
  },

  sidebar: {
    width: 340,
    minWidth: 300,
    borderRight: "1px solid rgba(0,0,0,0.12)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      minWidth: "100%",
    },
  },


    sidebarMobileHidden: {
      [theme.breakpoints.down("sm")]: {
        display: "none",
      },
    },
  userSelectBox: {
    padding: theme.spacing(1),
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },

  warningBox: {
    padding: theme.spacing(1),
    background: "#fff8e1",
    borderBottom: "1px solid #ffe082",
  },

  conversationsList: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },

  selectedConversation: {
    backgroundColor: "rgba(37, 118, 210, 0.08)",
  },

  chatArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },


    chatAreaMobileOpen: {
      [theme.breakpoints.down("sm")]: {
        display: "flex",
        width: "100%",
        minWidth: "100%",
      },
    },

    mobileBackButton: {
      display: "none",
      [theme.breakpoints.down("sm")]: {
        display: "inline-flex",
        marginRight: theme.spacing(1),
      },
    },
  chatHeader: {
    padding: theme.spacing(1.5),
    borderBottom: "1px solid rgba(0,0,0,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  messagesArea: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    backgroundColor: theme.palette.background.default,
    ...theme.scrollbarStyles,
  },

  emptyState: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(3),
  },

  messageRowMine: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(1),
  },

  messageRowOther: {
    display: "flex",
    justifyContent: "flex-start",
    marginBottom: theme.spacing(1),
  },

  messageBubbleMine: {
    maxWidth: "72%",
    padding: theme.spacing(1),
    borderRadius: 10,
    backgroundColor: "#dcf8c6",
    wordBreak: "break-word",
  },

  messageBubbleOther: {
    maxWidth: "72%",
    padding: theme.spacing(1),
    borderRadius: 10,
    backgroundColor: "#fff",
    wordBreak: "break-word",
  },

  messageMeta: {
    marginTop: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },

  attachmentLink: {
    marginTop: theme.spacing(1),
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: theme.palette.primary.main,
    textDecoration: "none",
  },

  composer: {
    padding: theme.spacing(1),
    borderTop: "1px solid rgba(0,0,0,0.12)",
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },

  hiddenInput: {
    display: "none",
  },
}));

const getBackendBaseUrl = () => {
  return api.defaults.baseURL || "";
};

const getAttachmentUrl = (attachment) => {
  if (!attachment || !attachment.filePath) return "#";

  const cleanPath = String(attachment.filePath).replace(/^\/+/, "");
  return `${getBackendBaseUrl()}/${cleanPath}`;
};

const getConversationTitle = (conversation, loggedUserId) => {
  if (!conversation) return "Chat Interno";

  if (conversation.title) return conversation.title;

  const participants = conversation.participants || [];
  const otherParticipant = participants.find(
    (participant) => Number(participant.userId) !== Number(loggedUserId)
  );

  if (otherParticipant && otherParticipant.user) {
    return otherParticipant.user.name;
  }

  return `Conversa #${conversation.id}`;
};

const getConversationUnread = (conversation, loggedUserId) => {
  const participants = conversation.participants || [];
  const participant = participants.find(
    (item) => Number(item.userId) === Number(loggedUserId)
  );

  return participant ? Number(participant.unreadCount || 0) : 0;
};


const getConversationEmailRecipients = (conversation) => {
  if (!conversation || !conversation.participants) return [];

  return conversation.participants
    .filter((participant) => participant.user && participant.user.email)
    .map((participant) => ({
      id: Number(participant.userId),
      name: participant.user.name,
      email: participant.user.email,
    }));
};

const formatDateTime = (value) => {
  if (!value) return "";

  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch (error) {
    return "";
  }
};

const InternalChat = () => {
  const classes = useStyles();
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const recordingTimeoutRef = useRef(null);

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState("");
  const [users, setUsers] = useState([]);
  const [targetUserId, setTargetUserId] = useState("");
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageBody, setMessageBody] = useState("");
  const [sending, setSending] = useState(false);
  const [recordingAudio, setRecordingAudio] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailUserIds, setSelectedEmailUserIds] = useState([]);
  const [sendingEmailPdf, setSendingEmailPdf] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editingBody, setEditingBody] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState(null);

  const loggedUserId = Number(user && user.id);

  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/internal-chat/conversations");
      setWarning(data.warning || "");
      setConversations(data.conversations || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users/", {
        params: {
          pageNumber: 1,
          searchParam: "",
        },
      });

      setUsers((data.users || []).filter((item) => Number(item.id) !== loggedUserId));
    } catch (err) {
      toastError(err);
    }
  }, [loggedUserId]);

  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;

    try {
      const { data } = await api.get(
        `/internal-chat/conversations/${conversationId}/messages`
      );
      setMessages(data.messages || []);
      scrollToBottom();
    } catch (err) {
      toastError(err);
    }
  }, []);

  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    await loadMessages(conversation.id);

    try {
      await api.put(`/internal-chat/conversations/${conversation.id}/read`);
      await loadConversations();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCreateDirectConversation = async () => {
    if (!targetUserId) {
      toast.warn("Selecione um usuário para iniciar a conversa.");
      return;
    }

    try {
      const { data } = await api.post("/internal-chat/conversations/direct", {
        targetUserId,
      });

      setTargetUserId("");
      await loadConversations();
      await handleSelectConversation(data);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSendText = async () => {
    if (!selectedConversation) return;

    const cleanBody = messageBody.trim();

    if (!cleanBody) return;

    setSending(true);

    try {
      const { data } = await api.post(
        `/internal-chat/conversations/${selectedConversation.id}/messages`,
        {
          body: cleanBody,
        }
      );

      setMessages((prev) => [...prev, data]);
      setMessageBody("");
      await loadConversations();
      scrollToBottom();
    } catch (err) {
      toastError(err);
    }

    setSending(false);
  };

  const uploadInternalFile = async (file) => {
    if (!selectedConversation || !file) return;

    const formData = new FormData();
    formData.append("file", file);

    setSending(true);

    try {
      const { data } = await api.post(
        `/internal-chat/conversations/${selectedConversation.id}/attachments`,
        formData
      );

      setMessages((prev) => [...prev, data]);
      await loadConversations();
      scrollToBottom();
    } catch (err) {
      toastError(err);
    }

    setSending(false);
  };

  const handleUploadFile = async (event) => {
    if (!selectedConversation) return;

    const file = event.target.files && event.target.files[0];
    event.target.value = "";

    if (!file) return;

    await uploadInternalFile(file);
  };

  const clearRecordingTimeout = () => {
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
  };

  const stopAudioTracks = () => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }
  };

  const resetAudioRecording = () => {
    clearRecordingTimeout();
    setRecordingAudio(false);
    stopAudioTracks();
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
  };

  const stopCurrentAudioRecording = () => {
    const recorder = mediaRecorderRef.current;

    if (!recorder) {
      resetAudioRecording();
      return;
    }

    try {
      if (recorder.state && recorder.state !== "inactive") {
        recorder.stop();
      } else {
        resetAudioRecording();
      }
    } catch (err) {
      resetAudioRecording();
      toast.warn("Gravação de áudio interrompida.");
    }
  };

  const handleToggleAudioRecording = async () => {
    if (!selectedConversation) return;

    if (recordingAudio) {
      stopCurrentAudioRecording();
      return;
    }

    if (sending) return;

    if (
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia ||
      !window.MediaRecorder
    ) {
      toast.warn("Seu navegador não permite gravação de áudio.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];

      let options;
      if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        options = { mimeType: "audio/webm;codecs=opus" };
      } else if (MediaRecorder.isTypeSupported("audio/webm")) {
        options = { mimeType: "audio/webm" };
      }

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = () => {
        resetAudioRecording();
        toast.warn("Erro na gravação de áudio.");
      };

      mediaRecorder.onstop = async () => {
        clearRecordingTimeout();
        setRecordingAudio(false);
        stopAudioTracks();

        const chunks = audioChunksRef.current;
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;

        const audioBlob = new Blob(chunks, {
          type: mediaRecorder.mimeType || "audio/webm",
        });

        if (!audioBlob.size) return;

        const audioFile = new File(
          [audioBlob],
          `audio-interno-${Date.now()}.webm`,
          { type: audioBlob.type || "audio/webm" }
        );

        await uploadInternalFile(audioFile);
      };

      mediaRecorder.start(1000);
      setRecordingAudio(true);

      recordingTimeoutRef.current = setTimeout(() => {
        stopCurrentAudioRecording();
      }, 180000);
    } catch (err) {
      resetAudioRecording();
      toast.warn("Não foi possível acessar o microfone.");
    }
  };

  const handleToggleImportant = async (message) => {
    try {
      const { data } = await api.put(
        `/internal-chat/messages/${message.id}/important`,
        {
          isImportant: !message.isImportant,
        }
      );

      setMessages((prev) =>
        prev.map((item) => (item.id === data.id ? data : item))
      );
    } catch (err) {
      toastError(err);
    }
  };


  const handleOpenEditMessage = (message) => {
    setEditingMessage(message);
    setEditingBody(message.body || "");
  };

  const handleCloseEditMessage = () => {
    if (savingEdit) return;

    setEditingMessage(null);
    setEditingBody("");
  };

  const handleSaveEditMessage = async () => {
    if (!editingMessage) return;

    const cleanBody = editingBody.trim();

    if (!cleanBody) {
      toast.warn("Digite uma mensagem para salvar.");
      return;
    }

    setSavingEdit(true);

    try {
      const { data } = await api.put(`/internal-chat/messages/${editingMessage.id}`, {
        body: cleanBody,
      });

      setMessages((prev) =>
        prev.map((item) => (item.id === data.id ? data : item))
      );

      toast.success("Mensagem editada com sucesso.");
      setEditingMessage(null);
      setEditingBody("");
    } catch (err) {
      toastError(err);
    }

    setSavingEdit(false);
  };

  const handleDeleteMessage = async (message) => {
    if (!message || message.isDeleted) return;

    const confirmed = window.confirm(
      "Deseja excluir esta mensagem? Ela será marcada como excluída, sem apagar o registro do histórico."
    );

    if (!confirmed) return;

    setDeletingMessageId(message.id);

    try {
      const { data } = await api.delete(`/internal-chat/messages/${message.id}`);

      setMessages((prev) =>
        prev.map((item) => (item.id === data.id ? data : item))
      );

      toast.success("Mensagem excluída com sucesso.");
    } catch (err) {
      toastError(err);
    }

    setDeletingMessageId(null);
  };


  const handleExportPdf = async () => {
    if (!selectedConversation) return;

    setExportingPdf(true);

    try {
      const response = await api.get(
        `/internal-chat/conversations/${selectedConversation.id}/export-pdf`,
        {
          responseType: "blob",
        }
      );

      const contentDisposition = response.headers["content-disposition"] || "";
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename =
        filenameMatch && filenameMatch[1]
          ? filenameMatch[1]
          : `conversa-interna-${selectedConversation.id}.pdf`;

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      toast.success("PDF exportado com sucesso.");
    } catch (err) {
      toastError(err);
    }

    setExportingPdf(false);
  };


  const handleOpenEmailDialog = () => {
    if (!selectedConversation) return;

    const recipients = getConversationEmailRecipients(selectedConversation);
    const defaultRecipients = recipients
      .filter((recipient) => Number(recipient.id) !== loggedUserId)
      .map((recipient) => recipient.id);

    setSelectedEmailUserIds(defaultRecipients);
    setEmailDialogOpen(true);
  };

  const handleToggleEmailRecipient = (userId) => {
    setSelectedEmailUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((item) => item !== userId);
      }

      return [...prev, userId];
    });
  };

  const handleSendPdfEmail = async () => {
    if (!selectedConversation) return;

    if (!selectedEmailUserIds.length) {
      toast.warn("Selecione pelo menos um destinatário.");
      return;
    }

    setSendingEmailPdf(true);

    try {
      const { data } = await api.post(
        `/internal-chat/conversations/${selectedConversation.id}/send-pdf-email`,
        {
          toUserIds: selectedEmailUserIds,
        }
      );

      const sentTo = (data.sentTo || [])
        .map((recipient) => recipient.email)
        .join(", ");

      toast.success(
        sentTo
          ? `PDF enviado por e-mail para: ${sentTo}`
          : "PDF enviado por e-mail com sucesso."
      );

      setEmailDialogOpen(false);
    } catch (err) {
      toastError(err);
    }

    setSendingEmailPdf(false);
  };

  useEffect(() => {
    setLoading(true);

    Promise.all([loadConversations(), loadUsers()]).finally(() => {
      setLoading(false);
    });
  }, [loadConversations, loadUsers]);

  useEffect(() => {
    const socket = openSocket();

    socket.emit("internalNotification");

    socket.on("internalNotification", () => {
      loadConversations();
    });

    socket.on("internalMessage", (data) => {
      if (
        selectedConversation &&
        data.message &&
        Number(data.message.conversationId) === Number(selectedConversation.id)
      ) {
        setMessages((prev) => {
          const exists = prev.some((item) => item.id === data.message.id);

          if (exists) {
            return prev.map((item) =>
              item.id === data.message.id ? data.message : item
            );
          }

          return [...prev, data.message];
        });

        scrollToBottom();
      }

      loadConversations();
    });

    socket.on("internalMessageRead", () => {
      loadConversations();
    });

    return () => {
      socket.disconnect();
    };
  }, [loadConversations, selectedConversation]);

  useEffect(() => {
    if (!selectedConversation) return;

    const socket = openSocket();
    socket.emit("joinInternalChat", selectedConversation.id);

    return () => {
      socket.disconnect();
    };
  }, [selectedConversation]);

  return (
    <MainContainer>
      <Dialog
        open={Boolean(editingMessage)}
        onClose={handleCloseEditMessage}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar mensagem</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            variant="outlined"
            value={editingBody}
            disabled={savingEdit}
            onChange={(event) => setEditingBody(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditMessage} disabled={savingEdit}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveEditMessage}
            color="primary"
            variant="contained"
            disabled={savingEdit || !editingBody.trim()}
          >
            {savingEdit ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
      <MainHeader>
        <Title>Chat Interno</Title>
        <MainHeaderButtonsWrapper>
          <Tooltip title="Atualizar">
            <IconButton onClick={loadConversations}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Dialog
        open={emailDialogOpen}
        onClose={() => !sendingEmailPdf && setEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enviar PDF por e-mail</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary">
            Selecione os destinatários usando os e-mails corporativos cadastrados no acesso de cada usuário.
          </Typography>

          <Box mt={2}>
            {getConversationEmailRecipients(selectedConversation).map((recipient) => (
              <FormControlLabel
                key={recipient.id}
                control={
                  <Checkbox
                    color="primary"
                    checked={selectedEmailUserIds.includes(recipient.id)}
                    onChange={() => handleToggleEmailRecipient(recipient.id)}
                  />
                }
                label={`${recipient.name} <${recipient.email}>`}
              />
            ))}

            {getConversationEmailRecipients(selectedConversation).length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                Nenhum participante com e-mail cadastrado nesta conversa.
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEmailDialogOpen(false)}
            disabled={sendingEmailPdf}
          >
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSendPdfEmail}
            disabled={sendingEmailPdf || !selectedEmailUserIds.length}
          >
            {sendingEmailPdf ? "Enviando..." : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={`${classes.sidebar} ${selectedConversation ? classes.sidebarMobileHidden : ""}`}>
          {warning ? (
            <div className={classes.warningBox}>
              <Typography variant="caption">{warning}</Typography>
            </div>
          ) : null}

          <div className={classes.userSelectBox}>
            <Select
              fullWidth
              displayEmpty
              variant="outlined"
              size="small"
              value={targetUserId}
              onChange={(event) => setTargetUserId(event.target.value)}
            >
              <MenuItem value="">
                <em>Selecionar usuário</em>
              </MenuItem>
              {users.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
            <Button
              color="primary"
              variant="contained"
              onClick={handleCreateDirectConversation}
              disabled={!targetUserId}
            >
              Abrir
            </Button>
          </div>

          <Divider />

          <List className={classes.conversationsList}>
            {conversations.map((conversation) => {
              const unread = getConversationUnread(conversation, loggedUserId);
              const selected =
                selectedConversation &&
                Number(selectedConversation.id) === Number(conversation.id);

              return (
                <ListItem
                  button
                  key={conversation.id}
                  selected={selected}
                  className={selected ? classes.selectedConversation : undefined}
                  onClick={() => handleSelectConversation(conversation)}
                >
                  <ListItemAvatar>
                    <Badge
                      color="secondary"
                      badgeContent={unread}
                      invisible={!unread}
                    >
                      <Avatar>
                        <ChatIcon />
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={getConversationTitle(conversation, loggedUserId)}
                    secondary={
                      conversation.messages && conversation.messages[0]
                        ? conversation.messages[0].body
                        : "Sem mensagens ainda"
                    }
                  />
                </ListItem>
              );
            })}

            {!loading && conversations.length === 0 ? (
              <Box p={2}>
                <Typography variant="body2" color="textSecondary">
                  Nenhuma conversa interna encontrada.
                </Typography>
              </Box>
            ) : null}
          </List>
        </div>

        <div className={`${classes.chatArea} ${selectedConversation ? classes.chatAreaMobileOpen : ""}`}>
          {!selectedConversation ? (
            <div className={classes.emptyState}>
              <Typography variant="body1" color="textSecondary">
                Selecione ou abra uma conversa interna para começar.
              </Typography>
            </div>
          ) : (
            <>
              <div className={classes.chatHeader}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    className={classes.mobileBackButton}
                    onClick={() => setSelectedConversation(null)}
                    size="small"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <div>
                    <Typography variant="h6">
                      {getConversationTitle(selectedConversation, loggedUserId)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Conversa interna privada entre participantes.
                    </Typography>
                  </div>
                </div>
                <Box display="flex" alignItems="center" gridGap={8}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={exportingPdf}
                    onClick={handleExportPdf}
                  >
                    {exportingPdf ? "Exportando..." : "Exportar PDF"}
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    disabled={sendingEmailPdf}
                    onClick={handleOpenEmailDialog}
                  >
                    Enviar PDF por e-mail
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      api
                        .put(
                          `/internal-chat/conversations/${selectedConversation.id}/read`
                        )
                        .then(loadConversations)
                        .catch(toastError)
                    }
                  >
                    Marcar como lida
                  </Button>
                </Box>
              </div>

              <div className={classes.messagesArea}>
                {messages.map((message) => {
                  const mine = Number(message.senderUserId) === loggedUserId;
                  const attachments = message.attachments || [];
                  const isDeleted = Boolean(message.isDeleted);
                  const canEdit =
                    mine && !isDeleted && message.messageType === "text";
                  const canDelete = mine && !isDeleted;

                  return (
                    <div
                      key={message.id}
                      className={
                        mine ? classes.messageRowMine : classes.messageRowOther
                      }
                    >
                      <div
                        className={
                          mine
                            ? classes.messageBubbleMine
                            : classes.messageBubbleOther
                        }
                      >
                        {!mine && message.sender ? (
                          <Typography variant="caption" color="primary">
                            {message.sender.name}
                          </Typography>
                        ) : null}

                        {isDeleted ? (
                          <Typography variant="body2" color="textSecondary">
                            <em>Mensagem excluída pelo autor.</em>
                          </Typography>
                        ) : (
                          <Typography variant="body2">{message.body}</Typography>
                        )}

                        {!isDeleted &&
                          attachments.map((attachment) => (
                            <a
                              key={attachment.id}
                              className={classes.attachmentLink}
                              href={getAttachmentUrl(attachment)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <InsertDriveFileIcon fontSize="small" />
                              {attachment.originalName || attachment.fileName}
                            </a>
                          ))}

                        <div className={classes.messageMeta}>
                          <Typography variant="caption" color="textSecondary">
                            {formatDateTime(message.createdAt)}
                            {message.isEdited && !isDeleted ? " • editada" : ""}
                            {isDeleted ? " • excluída" : ""}
                          </Typography>

                          <div className={classes.messageActions}>
                            {canEdit ? (
                              <Tooltip title="Editar mensagem">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEditMessage(message)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}

                            {canDelete ? (
                              <Tooltip title="Excluir mensagem">
                                <IconButton
                                  size="small"
                                  disabled={deletingMessageId === message.id}
                                  onClick={() => handleDeleteMessage(message)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}

                            {!isDeleted ? (
                              <Tooltip
                                title={
                                  message.isImportant
                                    ? "Remover importante"
                                    : "Marcar importante"
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleImportant(message)}
                                >
                                  {message.isImportant ? (
                                    <StarIcon fontSize="small" />
                                  ) : (
                                    <StarBorderIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <div className={classes.composer}>
                <input
                  className={classes.hiddenInput}
                  id="internal-chat-file"
                  type="file"
                  onChange={handleUploadFile}
                />
                <label htmlFor="internal-chat-file">
                  <IconButton component="span" disabled={sending || recordingAudio}>
                    <AttachFileIcon />
                  </IconButton>
                </label>

                <Tooltip title={recordingAudio ? "Parar gravação" : "Gravar áudio"}>
                  <IconButton
                    color={recordingAudio ? "secondary" : "default"}
                    disabled={sending && !recordingAudio}
                    onClick={handleToggleAudioRecording}
                  >
                    {recordingAudio ? <StopIcon /> : <MicIcon />}
                  </IconButton>
                </Tooltip>

                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder={recordingAudio ? "Gravando áudio..." : "Digite uma mensagem interna"}
                  value={messageBody}
                  disabled={sending}
                  onChange={(event) => setMessageBody(event.target.value)}
                  onKeyPress={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      handleSendText();
                    }
                  }}
                />

                <IconButton
                  color="primary"
                  disabled={sending || recordingAudio || !messageBody.trim()}
                  onClick={handleSendText}
                >
                  <SendIcon />
                </IconButton>
              </div>
            </>
          )}
        </div>
      </Paper>
    </MainContainer>
  );
};

export default InternalChat;
